import { Fragment, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { MdCancel } from 'react-icons/md';
import 'react-phone-number-input/style.css';
import { toast } from 'react-toastify';
import OutlineBtn from 'components/btns/OutlineBtn';
import useApiHook from 'hooks/useApiHook';
import Spiner from 'helper/Spiner';

export default function LogosUploadModal({
  isDefault,
  isViewModal,
  setIsViewModal,
  ModalHeader,
  logoImages,
  setLogoImages,
}) {
  const cancelButtonRef = useRef(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const { handleApiCall, isApiLoading } = useApiHook();

  const expectedAspectRatios = ['1:1', '3:1', '3:1'];

  const handleImageUpload = async (index, event) => {
    const file = event.target.files[0];
    if (file) {
      setFileInputKey(fileInputKey + 1);
      // Read the file as a data URL
      const dataURL = await readFileAsync(file);
      // Create an image element to get the natural dimensions
      const img = new Image();
      img.src = dataURL;

      img.onload = () => {
        const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b));
        const gcdValue = gcd(img.width, img.height);
        const actualAspectRatio = `${img.width / gcdValue}:${
          img.height / gcdValue
        }`;

        if (actualAspectRatio !== expectedAspectRatios[index])
          toast.error('Image dimensions are incorrect. Please upload again.');
        else if (file.size > 10485760)
          // 10 mb
          toast.error('Image size must be less than 10 MB.');
        else if (!file.type.startsWith('image/'))
          toast.error('Only images are allowed.');
        else if (!isDefault) {
          setLogoImages(index, file, dataURL);
        } else {
          setLogoImages((prevAdImages) => ({
            ...prevAdImages,
            [index]: { file, dataURL },
          }));
          uploadImage(index, file);
        }
      };
    }
  };

  const uploadImage = async (index, file) => {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('index', index);
    const result = await handleApiCall({
      method: 'post',
      url: `/admin/logos/change`,
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    if (result?.status === 200) {
      toast.success(result?.data?.message);
    }
  };

  const readFileAsync = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => resolve(event.target.result);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  const imagesTypes = [
    { dimension: '1:1', type: 'Logo Icon' },
    { dimension: '3:1', type: 'Dark background' },
    { dimension: '3:1', type: 'Light background' },
  ];

  return (
    <Transition.Root show={isViewModal} as={Fragment}>
      <Dialog
        as='div'
        className='relative z-10'
        initialFocus={cancelButtonRef}
        onClose={() => setIsViewModal(true)}
      >
        <Transition.Child
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-200'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'
        >
          <div className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity' />
        </Transition.Child>

        <div className='fixed inset-0 z-10 overflow-y-auto'>
          <div className='flex min-h-full items-center justify-center py-4 text-center sm:items-center sm:p-0'>
            <Transition.Child
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
              enterTo='opacity-100 translate-y-0 sm:scale-100'
              leave='ease-in duration-200'
              leaveFrom='opacity-100 translate-y-0 sm:scale-100'
              leaveTo='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
            >
              <Dialog.Panel className='w-full relative transform overflow-hidden bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl'>
                <div className='bg-white p-0'>
                  <div className='ez__PlanTripHead w-full bg-ezGreen p-4 md:px-6 flex items-center justify-between'>
                    <p className='text-white'>{ModalHeader}</p>
                    <button
                      type='button'
                      onClick={() => {
                        setIsViewModal(false);
                      }}
                      className='bg-white text-ezGreen rounded-full block w-max'
                    >
                      <MdCancel className='w-8 h-8' />
                    </button>
                  </div>
                  <div className='flex justify-center items-center h-9 pt-4'>
                    {isApiLoading && <Spiner color={'ezGreen'} />}
                  </div>
                  <div className='grid grid-cols-4 px-5 pb-5'>
                    {imagesTypes.map((value, index) => (
                      <div
                        key={index + 1}
                        className='flex flex-col p-5 col-span-3 text-ezGray'
                      >
                        {logoImages[index]?.dataURL ? (
                          <div className='mt-2'>
                            <label htmlFor=''>
                              Upload image in{' '}
                              <strong className='text-black'>
                                {value.dimension}
                              </strong>{' '}
                              Dimensions ({value.type})
                            </label>
                            <div className='flex flex-row items-center mt-2'>
                              <div
                                className={
                                  index === 1
                                    ? 'bg-gray-800 p-2'
                                    : 'bg-gray-200 p-2'
                                }
                              >
                                <img
                                  src={logoImages[index]?.dataURL}
                                  alt={`Uploaded ${index}`}
                                  className='object-scale-down w-32 h-32'
                                />
                              </div>
                              <div className='relative w-1/2 hover:cursor-pointer'>
                                <label
                                  className='ml-4 text-ezRed text-sm hover:cursor-pointer'
                                  htmlFor={`image-upload-${index}`}
                                >
                                  <p className='relative pl-4 pb-4 hover:cursor-pointer w-full'>
                                    Change Image
                                  </p>
                                  <input
                                    id={`image-upload-${index}`}
                                    key={fileInputKey}
                                    type='file'
                                    accept='image/*'
                                    className='absolute top-0 left-0 opacity-0 cursor-pointer w-full h-full'
                                    onChange={(event) =>
                                      handleImageUpload(index, event)
                                    }
                                  />
                                </label>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <>
                            <label className='mb-2' htmlFor=''>
                              Upload image in{' '}
                              <strong className='text-black'>
                                {value.dimension}
                              </strong>{' '}
                              Dimension ({value.type})
                            </label>
                            <div className='relative w-1/3'>
                              <label
                                className='block'
                                htmlFor={`image-upload-${index}`}
                              >
                                <OutlineBtn
                                  btnType='button'
                                  text='Upload Image'
                                  className={'text-sm px-3 py-1 w-full'}
                                />
                                <input
                                  id={`image-upload-${index}`}
                                  key={fileInputKey}
                                  type='file'
                                  accept='image/*'
                                  className='absolute top-0 left-0 opacity-0 cursor-pointer w-full h-full'
                                  onChange={(event) =>
                                    handleImageUpload(index, event)
                                  }
                                />
                              </label>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
